import React from "react";
import { Eye, EyeOff } from "lucide-react";
import { PRESET_LABELS, type RangePreset, type SocialRange, todaySantiago } from "./socialRange";

interface DateRangeProps {
  range: SocialRange;
  onChange: (range: SocialRange) => void;
}

/** Selector de ventana de todo el tab Social: KPIs, cola y tabla. Las fechas del rango personalizado van aparte. */
export function SocialDateRange({ range, onChange }: DateRangeProps) {
  const today = todaySantiago();
  return (
    <div className="social-range">
      <select
        className="compact-select"
        aria-label="Ventana de análisis"
        value={range.preset}
        onChange={(event) => {
          const preset = event.target.value as RangePreset;
          onChange(preset === "custom" ? { preset, desde: range.desde, hasta: range.hasta } : { preset });
        }}
      >
        {(Object.keys(PRESET_LABELS) as RangePreset[]).map((key) => (
          <option key={key} value={key}>
            {PRESET_LABELS[key]}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Fechas del rango personalizado: en el cuerpo del tab, no en el header, para no desplazar las pestañas. */
export function SocialCustomDates({ range, onChange }: DateRangeProps) {
  const today = todaySantiago();
  return (
    <div className="social-custom-dates">
      <label>
        Desde
        <input
          type="date"
          className="compact-select"
          value={range.desde ?? ""}
          max={range.hasta || today}
          onChange={(event) => onChange({ ...range, desde: event.target.value })}
        />
      </label>
      <label>
        Hasta
        <input
          type="date"
          className="compact-select"
          value={range.hasta ?? ""}
          min={range.desde}
          max={today}
          onChange={(event) => onChange({ ...range, hasta: event.target.value })}
        />
      </label>
    </div>
  );
}

interface AuditToggleProps {
  hidden: boolean;
  onToggle: () => void;
}

/** Oculta o muestra el panel de auditoría de Gertrudis. */
export function SocialAuditToggle({ hidden, onToggle }: AuditToggleProps) {
  return (
    <button
      type="button"
      className="button button-ghost button-sm social-audit-toggle"
      onClick={onToggle}
      title={hidden ? "Mostrar auditoría de Gertrudis" : "Ocultar auditoría de Gertrudis"}
    >
      {hidden ? <Eye size={14} /> : <EyeOff size={14} />}
      {hidden ? "Mostrar Gertrudis" : "Ocultar Gertrudis"}
    </button>
  );
}

interface EmojiToggleProps {
  active: boolean;
  onToggle: (active: boolean) => void;
  count?: number;
}

/** Toggle on/off para filtrar comentarios que contienen emojis, situado al lado del selector de fechas. */
export function SocialEmojiToggle({ active, onToggle, count }: EmojiToggleProps) {
  return (
    <button
      type="button"
      className={`social-emoji-toggle ${active ? "is-active" : ""}`}
      onClick={() => onToggle(!active)}
      aria-pressed={active}
      title={
        active
          ? "Filtro activo: mostrando solo comentarios con emoji (clic para ver todos)"
          : "Filtrar comentarios que contienen emoji"
      }
    >
      <span className="emoji-icon" aria-hidden="true">
        😊
      </span>
      <span className="emoji-label">Contiene emoji</span>
      {count !== undefined && count > 0 && (
        <span className="emoji-count-badge">{count}</span>
      )}
      <span className="emoji-switch-track" aria-hidden="true">
        <span className="emoji-switch-thumb" />
      </span>
    </button>
  );
}

